import * as React from "react"
import * as Antd from "antd"
import * as AntdIcons from "@ant-design/icons"
import styles from "./PremiumAssistantChat.module.scss"

import { AppLanguages } from "@constants/AppLanguages.ts"
import { AppFunctions } from "@others/AppFunctions.ts"
import { useTranslation } from "react-i18next"
import { useLoggedUserData } from "@hooks/useLoggedUserData.ts"
import { Channel as StreamChannel } from "stream-chat"
import { DefaultGenerics, StreamChat, TranslationLanguages } from "stream-chat"

import {
	Chat,
	Channel,
	Window,
	MessageList,
	MessageInput,
	Streami18n,
} from "stream-chat-react"

import AppText from "@components/general/AppText/AppText.tsx"
import DsButton from "@components/general/DsButton/DsButton.tsx"
import SendButton from "@components/others/ChatWidget/PremiumAssistantChat/SendButton/SendButton.tsx"
import Animations from "@components/others/Animations/Animations.tsx"
import userService from "@services/userService/userService.ts"
import FileUploadIcon from "@components/others/ChatWidget/PremiumAssistantChat/FileUploadIcon/FileUploadIcon.tsx"
import EmptyStateIndicator from "@components/others/ChatWidget/PremiumAssistantChat/EmptyStateIndicator/EmptyStateIndicator.tsx"
import useResponsiveToolCtx from "@contexts/hooks/useResponsiveToolCtx.tsx"

export interface IChatProps {
	className?: string
	onClose?: () => void
	style?: React.CSSProperties
	onUnreadMessage?: (amount: number) => void
	visible: boolean
}

const PremiumAssistantChat: React.FC<IChatProps> = (props) => {
	const { i18n, t } = useTranslation()
	const { isMobile } = useResponsiveToolCtx()
	const { loggedUser } = useLoggedUserData()

	const clientRef = React.useRef<StreamChat<DefaultGenerics>>()
	const clientRefInitialized = React.useRef(false)
	const channelRef = React.useRef<StreamChannel<DefaultGenerics>>()
	const streamChatRef = React.useRef<HTMLDivElement>(null)
	const i18nInstanceRef = React.useRef<Streami18n>()

	const [chatClient, setChatClient] =
		React.useState<StreamChat<DefaultGenerics>>()

	const checkUnread = () => {
		if (props.visible) return

		clientRef.current?.getUnreadCount().then((response) => {
			props.onUnreadMessage?.(response.total_unread_count)
		})
	}

	React.useEffect(() => {
		if (props.visible) props.onUnreadMessage?.(0)
	}, [props.visible])

	React.useEffect(() => {
		if (!clientRef.current) return

		const handleNewMessage = (event: any) => {
			if (event.message.user.id !== loggedUser?.id) {
				if (Notification.permission === "granted" && !props.visible) {
					new Notification(t("premium_assistant"), {
						body: event.message.text,
						icon: event.message.user.image,
					})
				}

				checkUnread()
			}
		}

		clientRef.current?.on("notification.message_new", handleNewMessage)
		clientRef.current?.on("message.new", handleNewMessage)

		return () => {
			clientRef.current?.off("notification.message_new", handleNewMessage)
			clientRef.current?.off("message.new", handleNewMessage)
		}
	}, [clientRef.current, checkUnread])

	React.useEffect(() => {
		i18nInstanceRef.current?.setLanguage(
			i18n.language as TranslationLanguages
		)
	}, [i18n.language])

	React.useEffect(() => {
		const initChat = async () => {
			if (clientRef.current) return

			clientRef.current = StreamChat.getInstance(
				AppFunctions.getViteEnv("VITE_STREAM_API_KEY")
			)

			await clientRef.current.connectUser(
				{
					id: loggedUser?.id!,
					name: loggedUser?.name,
					image: loggedUser?.profilePhotoUrl,
				},
				async () => {
					const { token } = await userService.getUserChatToken()
					return token || ""
				}
			)

			channelRef.current = clientRef.current?.channel(
				"messaging",
				loggedUser?.id,
				{
					members: [
						loggedUser?.id!,
						AppFunctions.getViteEnv(
							"VITE_STREAM_BACKOFFICE_USER_ID"
						),
					],
				}
			)

			checkUnread()

			i18nInstanceRef.current = new Streami18n({
				language: AppLanguages.DEFAULT as TranslationLanguages,
			})

			await i18nInstanceRef.current.setLanguage(
				i18n.language as TranslationLanguages
			)

			setChatClient(clientRef.current)
		}

		initChat().then(() => {
			clientRefInitialized.current = true
		})

		return () => {
			if (clientRefInitialized.current) {
				clientRef.current?.disconnectUser()
				setChatClient(undefined)
			}
		}
	}, [])

	return (
		<Animations
			ref={streamChatRef}
			show={props.visible}
			presets={isMobile ? "slide-in-out-bottom" : "scale-in-out-br"}
		>
			<div
				className={`${styles.premium_assistant_chat_container} ${props.className || ""}`}
				style={props.style}
				ref={streamChatRef}
			>
				<header className={styles.header}>
					<AppText
						className={styles.title}
						textKey="premium_assistant"
					/>

					<DsButton
						className={styles.button}
						type="text"
						onClick={props.onClose}
						icon={<AntdIcons.ShrinkOutlined />}
					/>
				</header>

				<div className={styles.main}>
					{chatClient && clientRef.current ? (
						<Chat
							client={clientRef.current}
							i18nInstance={i18nInstanceRef.current}
						>
							<Channel
								channel={channelRef.current}
								EmptyStateIndicator={EmptyStateIndicator}
								FileUploadIcon={FileUploadIcon}
								SendButton={SendButton}
							>
								<Window>
									<MessageList
										messageActions={[
											"edit",
											"delete",
											"quote",
											"react",
											"reply",
										]}
									/>
									<MessageInput />
								</Window>
							</Channel>
						</Chat>
					) : (
						<Antd.Spin className={styles.spin} />
					)}
				</div>
			</div>
		</Animations>
	)
}

export default React.memo(PremiumAssistantChat)
